/**
 * Per-point density for the performance scatter's Color By DENSITY (and the timeline strip under it).
 */

/**
 * Bins pixel-space points on a `cell` px grid and gives each point a value instead of each cell. A
 * point's count is its own cell plus half of each edge neighbour and a quarter of each corner
 * neighbour; without that, two points either side of a cell boundary could get very different
 * colours. Returns count / max in [0, 1], in input order. Non-finite points get 0.
 */
export function pointDensityFractions(
  pxPoints: ReadonlyArray<{ x?: number; y?: number }>,
  cell = 14
): Float64Array {
  const c = Math.max(2, cell);
  const out = new Float64Array(pxPoints.length);
  const counts = new Map<string, number>();
  const keys: Array<[number, number] | null> = pxPoints.map((p) => {
    const x = Number(p.x);
    const y = Number(p.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {return null;}
    const gx = Math.floor(x / c);
    const gy = Math.floor(y / c);
    const k = `${gx},${gy}`;
    counts.set(k, (counts.get(k) ?? 0) + 1);
    return [gx, gy];
  });
  const smoothed = new Map<string, number>();
  const smoothedAt = (gx: number, gy: number): number => {
    const k = `${gx},${gy}`;
    const cached = smoothed.get(k);
    if (cached !== undefined) {return cached;}
    let s = 0;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const n = counts.get(`${gx + dx},${gy + dy}`) ?? 0;
        if (n === 0) {continue;}
        const w = dx === 0 && dy === 0 ? 1 : dx === 0 || dy === 0 ? 0.5 : 0.25;
        s += n * w;
      }
    }
    smoothed.set(k, s);
    return s;
  };
  let max = 0;
  keys.forEach((g, i) => {
    if (!g) {return;}
    const v = smoothedAt(g[0], g[1]);
    out[i] = v;
    if (v > max) {max = v;}
  });
  if (max > 0) {
    for (let i = 0; i < out.length; i++) {out[i] = out[i]! / max;}
  }
  return out;
}
